/**
 * @module Model
 */

/**
 * Enum representing the available theme modes for the application.
 *
 * This enum defines two possible theme modes: Light and Dark. It can be used to specify
 * the current theme mode in the application settings or user preferences.
 *
 * @enum {number}
 */
export enum ThemeMode {
	Light,
	Dark,
}

/**
 * Interface representing a theme configuration.
 *
 * This interface defines the structure of a theme object, including its name, mode, and
 * whether it is currently active. The 'name' property should match the theme colors defined
 * in the application's SCSS variables.
 *
 * @interface
 */
export interface Theme {
	/**
	 * The name of the theme.
	 *
	 * This should match the $theme-colors() from the _variables.scss file.
	 *
	 * @type {string}
	 */
	name: string;

	/**
	 * The mode of the theme, which can be either Light or Dark.
	 *
	 * @type {ThemeMode}
	 */
	mode: ThemeMode;

	/**
	 * Indicates whether the theme is currently active.
	 *
	 * @type {boolean}
	 */
	active: boolean;
}

/**
 * Interface representing a loading state for various components or features.
 *
 * This interface allows for dynamic keys where each key corresponds to a feature or component
 * and its value indicates whether it is currently loading.
 *
 * @interface
 */
export interface IsLoadingType {
	[key: string]: boolean;
}

/**
 * Enum representing the types of headers used in the application.
 *
 * This enum defines different header types that can be utilized within the application,
 * such as headers for authorized users and landing pages.
 *
 * @enum {number}
 */
export enum HeaderType {
	FrontendAuthorizedUser,
	FrontendLandingPage,
}

/**
 * Enum representing the types of navigation components in the application.
 *
 * This enum defines various navigation types, including headers, side navigation, and footers.
 *
 * @enum {number}
 */
export enum NavigationType {
	Header,
	Sidenav,
	Footer,
}

/**
 * Enum representing the types of side navigation available in the application.
 *
 * This enum defines specific types of sidenav that can be used, such as default,
 * notifications, and filters for orders, users, and companies.
 *
 * @enum {number}
 */
export enum SidenavType {
	Default,
	Notifications,
	OrdersFilter,
	UsersFilter,
	CompaniesFilter
}

/**
 * Enum representing the possible positions for the side navigation.
 *
 * This enum defines the positions where the sidenav can be placed, either at the start or end
 * of the viewport.
 *
 * @enum {string}
 */
export enum SidenavPosition {
	End = 'end',
	Start = 'start'
}
